import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { apiFetch } from '../utils/api';
import { PageHeader, SearchBar, DataTable, Badge, Loading, formatCurrency, usePersistedState, BackButton } from '../components/Shared';
import { useAuth } from '../context/AuthContext';

function ModalFormation({ initial, onSave, onClose }) {
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    apiFetch('/formation-categories').then(d => setCategories((d || []).map(c => c.libelle))).catch(() => {});
  }, []);
  const [form, setForm] = useState(initial || {
    intitule: '', categorie: '', duree_heures: '', stagiaires_max: '',
    cout_stagiaire_ht: '', cout_groupe_ht: '', prerequis: '',
    objectifs: '', infos_complem: '', mention_reglementaire: '', note_attestation: '',
    notes: '', nom_dossier: '',
    code_pf: '', numero: '', recyclage: false, adaptation_psh: true,
    periode_recyclage_mois: '', actif: true, modele_attestation: 1,
  });
  const [numeroError, setNumeroError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isEdit = !!initial?.formation_id;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inputStyle = {
    width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px',
    border: '1px solid var(--border)', background: 'var(--bg-elevated)',
    color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box',
  };

  function field(label, key, type = 'text') {
    return (
      <div style={{ marginBottom: '0.7rem' }}>
        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>{label}</label>
        <input type={type} value={form[key] ?? ''} onChange={e => set(key, e.target.value)} style={inputStyle} />
      </div>
    );
  }

  function textarea(label, key) {
    return (
      <div style={{ marginBottom: '0.7rem' }}>
        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>{label}</label>
        <textarea value={form[key] || ''} onChange={e => set(key, e.target.value)} rows={3}
          style={{ ...inputStyle, resize: 'vertical' }} />
      </div>
    );
  }

  function checkbox(label, key) {
    return (
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.88rem', marginBottom: '0.7rem' }}>
        <input type="checkbox" checked={form[key] ?? false} onChange={e => set(key, e.target.checked)} />
        {label}
      </label>
    );
  }

  function validateNumero(val) {
    if (!val || !String(val).trim()) return 'Le N° de formation est obligatoire';
    return /^\d{1,2}(\.\d)?$/.test(String(val).trim()) ? '' : 'Format : X, XX ou XX.X (ex : 5, 12, 12.3)';
  }

  async function handleSave() {
    if (!form.intitule) return setError('L\'intitulé est requis');
    const numErr = validateNumero(form.numero);
    if (numErr) { setNumeroError(numErr); return; }
    setSaving(true); setError(''); setNumeroError('');
    try {
      if (isEdit) {
        await apiFetch(`/formations/${initial.formation_id}`, { method: 'PUT', body: JSON.stringify(form) });
      } else {
        await apiFetch('/formations', { method: 'POST', body: JSON.stringify(form) });
      }
      onSave();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '640px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)' }}>
          {isEdit ? `Modifier — ${initial.intitule?.substring(0, 50)}` : '+ Nouvelle formation'}
        </h3>

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: '0.7rem', letterSpacing: '0.06em' }}>Identification</p>
        {field('Intitulé *', 'intitule')}
        <div className="grid-2" style={{ gap: '0 0.7rem' }}>
          <div style={{ marginBottom: '0.7rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Catégorie</label>
            <select value={form.categorie || ''} onChange={e => set('categorie', e.target.value)} style={inputStyle}>
              <option value="">— Choisir —</option>
              {categories.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          {field('Nom abrégé (calendrier)', 'nom_dossier')}
        </div>
        <div className="grid-2" style={{ gap: '0 0.7rem' }}>
          <div style={{ marginBottom: '0.7rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>N° formation * <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(X, XX ou XX.X)</span></label>
            <input type="text" value={form.numero ?? ''} onChange={e => { set('numero', e.target.value); setNumeroError(validateNumero(e.target.value)); }} style={{ ...inputStyle, borderColor: numeroError ? '#f87171' : undefined }} placeholder="ex : 5, 12, 12.3" />
            {numeroError && <p style={{ fontSize: '0.75rem', color: '#f87171', marginTop: '0.2rem' }}>{numeroError}</p>}
          </div>
          {field('Code PF', 'code_pf')}
        </div>
        <div className="grid-2" style={{ gap: '0 0.7rem' }}>
          {field('Durée (heures)', 'duree_heures', 'number')}
          {field('Max stagiaires', 'stagiaires_max', 'number')}
        </div>

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Tarifs</p>
        <div className="grid-2" style={{ gap: '0 0.7rem' }}>
          {field('Coût / stagiaire HT (€)', 'cout_stagiaire_ht', 'number')}
          {field('Coût / groupe HT (€)', 'cout_groupe_ht', 'number')}
        </div>

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Contenu</p>
        {textarea('Objectifs', 'objectifs')}
        {textarea('Pré-requis', 'prerequis')}
        {textarea('Informations convocation', 'infos_complem')}
        {textarea('Mention réglementaire', 'mention_reglementaire')}
        {textarea('Note attestation', 'note_attestation')}
        {textarea('Notes internes', 'notes')}

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Options</p>
        <div className="grid-2" style={{ gap: '0 0.7rem' }}>
          <div>
            <div style={{ marginBottom: '0.7rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Recyclage obligatoire</label>
              <select value={form.recyclage ? 'oui' : 'non'} onChange={e => set('recyclage', e.target.value === 'oui')} style={inputStyle}>
                <option value="non">Non</option>
                <option value="oui">Oui</option>
              </select>
            </div>
            {checkbox('Adaptation PSH', 'adaptation_psh')}
            {checkbox('Formation active', 'actif')}
          </div>
          <div>
            {field('Période recyclage (mois)', 'periode_recyclage_mois', 'number')}
          </div>
        </div>

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Attestation de formation</p>
        <div style={{ marginBottom: '0.7rem' }}>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
            Modèle d'attestation
          </label>
          <select value={form.modele_attestation ?? 1} onChange={e => set('modele_attestation', parseInt(e.target.value))} style={inputStyle}>
            <option value={1}>Modèle 1 — Attestation de fin de formation</option>
            <option value={2}>Modèle 2 — Attestation de compétence (avec date de validité)</option>
            <option value={3}>Modèle 3</option>
          </select>
          <small style={{ color: 'var(--text-tertiary)', fontSize: '0.75rem' }}>
            Le modèle 2 affiche la période de recyclage et la date d'expiration de l'attestation.
          </small>
        </div>

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', borderRadius: '6px', padding: '0.5rem 0.8rem', color: '#f87171', fontSize: '0.85rem', marginBottom: '0.8rem' }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : isEdit ? '✓ Enregistrer' : '✓ Créer la formation'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CategoriesDropdown({ selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [allCats, setAllCats] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    apiFetch('/formation-categories').then(d => setAllCats((d || []).map(c => c.libelle))).catch(() => {});
  }, []);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function toggle(cat) {
    onChange(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  }

  const label = selected.length === 0
    ? 'Toutes catégories ▾'
    : `${selected.length} catégorie${selected.length > 1 ? 's' : ''} ▾`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className={`filter-chip ${selected.length > 0 ? 'active' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        {label}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          padding: '0.4rem 0', minWidth: '210px',
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.35rem 0.8rem', cursor: 'pointer', fontSize: '0.88rem' }}>
            <input type="checkbox" checked={selected.length === 0} onChange={() => onChange([])} />
            <span style={{ fontWeight: 600 }}>Toutes</span>
          </label>
          <div style={{ height: '1px', background: 'var(--border)', margin: '0.3rem 0' }} />
          {allCats.map(c => (
            <label key={c} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.35rem 0.8rem', cursor: 'pointer', fontSize: '0.88rem' }}>
              <input type="checkbox" checked={selected.includes(c)} onChange={() => toggle(c)} />
              <span>{c}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Formations() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [search, setSearch] = usePersistedState('formations_search', '');
  const [categories, setCategories] = usePersistedState('formations_categories', []);
  const [modal, setModal] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [archives, setArchives] = usePersistedState('formations_archives', false);

  const categorieParam = categories.join(',');
  const { data, loading } = useApi('/formations', { search, categorie: categorieParam, limit: 100, archives: archives ? 'true' : undefined }, [search, categorieParam, refresh, archives]);

  const columns = [
    { header: 'N°', render: r => r.numero || r.formation_id, width: '60px', mobileHide: true },
    { header: 'Intitulé', render: r => <strong>{r.intitule?.substring(0, 70)}{r.intitule?.length > 70 ? '...' : ''}</strong> },
    { header: 'Catégorie', render: r => <Badge variant="badge-neutral">{r.categorie}</Badge> },
    { header: 'Durée', render: r => `${r.duree_heures || 0}h` },
    { header: 'Max stag.', key: 'stagiaires_max', mobileHide: true },
    { header: 'Coût/stag.', render: r => formatCurrency(r.cout_stagiaire_ht), mobileHide: true },
    { header: 'Coût groupe', render: r => formatCurrency(r.cout_groupe_ht), mobileHide: true },
    { header: 'Nom abrégé', key: 'nom_dossier', mobileHide: true },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Catalogue Formations"
        subtitle={data ? `${data.total} formations` : ''}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <BackButton />
            {hasRole('utilisateur') && <button className="btn-primary" onClick={() => setModal(true)}>+ Nouvelle formation</button>}
          </div>
        }
      />

      <div className="toolbar">
        <SearchBar value={search} onChange={setSearch} placeholder="Rechercher une formation..." />
        <div className="toolbar-filters">
          <CategoriesDropdown selected={categories} onChange={setCategories} />
          <button className={`filter-chip ${archives ? 'active' : ''}`}
            onClick={() => setArchives(a => !a)}
            style={archives ? { borderColor: '#f87171', background: 'rgba(239,68,68,0.15)', color: '#f87171' } : {}}>
            🗄 Archivées
          </button>
        </div>
      </div>

      {loading ? <Loading /> : (
        <DataTable columns={columns} data={data?.data} onRowClick={r => navigate(`/formations/${r.formation_id}`)} />
      )}

      {modal && (
        <ModalFormation
          onSave={() => { setModal(false); setRefresh(r => r + 1); }}
          onClose={() => setModal(false)}
        />
      )}
    </div>
  );
}
